import React, { useCallback, useMemo, useState } from "react";
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RouteProp } from "@react-navigation/native";
import { ActivityState, IleCompletionStatus, useGetLmsLinkedUserIlEsQuery } from "@axio/graphql/generated/graphql";
import { StyleSheet, View } from "react-native";
import * as Sentry from "@sentry/react-native";
import LTILayout from "../layouts/LTILayout";
import type { LTIErrorCode, RootStackParamList } from "../navigation/navigation.types";
import { getAuthHeader } from "../utils/authRefresh";
import { useAuth } from "../hooks/useAuth";
import Loader from "../components/common/status/Loader/Loader";
import DeepLinkAutoSubmit from "../components/lti/DeepLinkAutoSubmit";
import AssessmentForm from "../components/customCoursework/ActivityForms/AssessmentForm/AssessmentForm";
import LTIErrorView from "./LTIErrorView";

type DeepLinkCompleteResponse = {
  jwt?: string;
  deepLinkReturnUrl?: string;
  error?: string;
};

type AssessmentSavePayload = {
  id?: string;
  assessmentId?: string;
  dueDate?: string | Date | null;
};

const DEFAULT_MAX_POINTS = 100;

const normalizeIntegrationId = (rawIntegrationId?: string | null): string | undefined => {
  if (!rawIntegrationId) {
    return undefined;
  }

  const trimmed = rawIntegrationId.trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") {
    return undefined;
  }

  return trimmed;
};

const normalizeDueDate = (rawDueDate?: string | Date | null): string | undefined => {
  if (!rawDueDate) {
    return undefined;
  }

  if (typeof rawDueDate === "string") {
    return rawDueDate;
  }

  if (rawDueDate instanceof Date && !Number.isNaN(rawDueDate.getTime())) {
    return rawDueDate.toISOString();
  }

  return undefined;
};

function LTIDeepLinkAssessmentView() {
  const route = useRoute<RouteProp<RootStackParamList, "LTIDeepLinkAssessment">>();
  const routeParams = route.params;
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { login } = useAuth();
  const [submitError, setSubmitError] = useState<{ code: LTIErrorCode; message: string } | null>(null);
  const [formError, setFormError] = useState("");
  const [isBootstrappingAuth, setIsBootstrappingAuth] = useState(true);
  const [isCompletingDeepLink, setIsCompletingDeepLink] = useState(false);
  const [deepLinkResponse, setDeepLinkResponse] = useState<{ jwt: string; deepLinkReturnUrl: string } | null>(null);

  const searchParams = useMemo(() => {
    if (typeof window === "undefined") {
      return new URLSearchParams();
    }
    return new URLSearchParams(window.location.search);
  }, []);

  const accessToken = routeParams?.accessToken ?? searchParams.get("accessToken") ?? undefined;
  const refreshToken = routeParams?.refreshToken ?? searchParams.get("refreshToken") ?? undefined;
  const deepLinkStateToken = routeParams?.deepLinkStateToken ?? searchParams.get("deepLinkStateToken") ?? undefined;
  const integrationId = normalizeIntegrationId(
    routeParams?.integrationId ?? searchParams.get("integrationId") ?? searchParams.get("integration_id")
  );
  const skipAuth = searchParams.get("skipAuth") === "true";
  const shouldFetchLmsLinkedIles = Boolean(integrationId) && !isBootstrappingAuth && !skipAuth;

  const { data: userIlesData, loading: ilesLoading } = useGetLmsLinkedUserIlEsQuery({
    variables: {
      // shouldFetchLmsLinkedIles already requires a truthy integrationId, and the
      // query is skipped otherwise, so the "" fallback is never actually sent — it
      // just satisfies the non-optional string variable type.
      integrationId: integrationId ?? "",
      completionStatus: IleCompletionStatus.Active,
    },
    skip: !shouldFetchLmsLinkedIles,
    fetchPolicy: "cache-and-network",
  });

  const lmsAiTutors = useMemo(() => {
    const raw = userIlesData?.getLmsLinkedUserILEs?.data;
    if (!raw) {
      return [];
    }
    return raw.flatMap((ile) => (ile ? [{ id: ile.id, title: ile.title }] : []));
  }, [userIlesData]);

  useFocusEffect(
    useCallback(() => {
      let isActive = true;

      const bootstrapAuth = async () => {
        // Skip auth bootstrap entirely if skipAuth=true (test mode)
        if (skipAuth) {
          if (isActive) {
            setIsBootstrappingAuth(false);
          }
          return;
        }

        if (!deepLinkStateToken) {
          if (isActive) {
            setIsBootstrappingAuth(false);
            setSubmitError({ code: "invalid_launch", message: "Missing deep-link launch context." });
          }
          return;
        }

        if (!accessToken || !refreshToken) {
          if (isActive) {
            setIsBootstrappingAuth(false);
          }
          return;
        }

        try {
          const authResult = await login(accessToken, refreshToken, { awaitUserData: false });
          if (!isActive) {
            return;
          }

          const isVerified = authResult.isEmailVerified && authResult.otpVerifiedForSession;
          if (!isVerified) {
            navigation.reset({
              index: 0,
              routes: [{ name: "Verify Email" }],
            });
            return;
          }

          setIsBootstrappingAuth(false);
        } catch (error) {
          if (!isActive) {
            return;
          }

          // For deep-link assessment, allow form render even if auth bootstrap fails.
          // Real token validation happens on deep-link-complete submission.
          // This allows testing with fake tokens.
          setIsBootstrappingAuth(false);
        }
      };

      bootstrapAuth();

      return () => {
        isActive = false;
      };
    }, [accessToken, deepLinkStateToken, refreshToken, login, navigation, skipAuth])
  );

  const handleAssessmentCreated = useCallback(
    async (payload: AssessmentSavePayload) => {
      const assessmentId = payload.assessmentId ?? payload.id;
      if (!assessmentId) {
        setSubmitError({ code: "unknown", message: "Assessment was created without an ID." });
        return;
      }

      if (!deepLinkStateToken) {
        setSubmitError({ code: "invalid_launch", message: "Missing deepLinkStateToken parameter." });
        return;
      }

      setSubmitError(null);
      setFormError("");
      setIsCompletingDeepLink(true);

      try {
        const authHeader = await getAuthHeader();
        const normalizedDueDate = normalizeDueDate(payload.dueDate);
        const title = "Assessment";

        const response = await fetch("/auth/lti/deep-link-complete", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeader,
          },
          credentials: "include",
          body: JSON.stringify({
            activityId: assessmentId,
            title,
            deepLinkStateToken,
            ...(normalizedDueDate ? { dueDate: normalizedDueDate } : {}),
            maxPoints: DEFAULT_MAX_POINTS,
            activityType: "ASSESSMENT",
          }),
        });

        const completionPayload = (await response.json().catch(() => null)) as DeepLinkCompleteResponse | null;

        if (!response.ok) {
          throw new Error(completionPayload?.error || "Failed to complete LTI deep link");
        }

        if (!completionPayload?.jwt || !completionPayload?.deepLinkReturnUrl) {
          throw new Error("Deep link completion response was missing required data");
        }

        setDeepLinkResponse({
          jwt: completionPayload.jwt,
          deepLinkReturnUrl: completionPayload.deepLinkReturnUrl,
        });
      } catch (error) {
        Sentry.captureException(error);
        const errorMessage = error instanceof Error ? error.message : "Failed to complete LTI deep link";
        setFormError(errorMessage);
        setSubmitError({ code: "unknown", message: errorMessage });
      } finally {
        setIsCompletingDeepLink(false);
      }
    },
    [deepLinkStateToken]
  );

  const handleCancel = useCallback(() => {
    setSubmitError({ code: "unknown", message: "Assessment deep-link flow was canceled." });
  }, []);

  if (!isBootstrappingAuth && submitError) {
    return <LTIErrorView error={submitError.code} message={submitError.message} />;
  }

  if (deepLinkResponse) {
    return (
      <LTILayout>
        <DeepLinkAutoSubmit jwt={deepLinkResponse.jwt} deepLinkReturnUrl={deepLinkResponse.deepLinkReturnUrl} />
      </LTILayout>
    );
  }
  // Hold the loader until auth bootstrap finishes AND the linked AI tutors used to
  // initialize the form have been fetched, so instructors never see a half-populated
  // form. Only gate on the tutors query before the first response: because it uses
  // cache-and-network, `ilesLoading` also goes true during background refetches when
  // cached tutors are already present, and blocking the form then would flicker. When
  // there is no integrationId the query is skipped (loading stays false), so the form
  // renders as soon as auth is ready.
  const isLoading = isBootstrappingAuth || (ilesLoading && !userIlesData);

  return (
    <LTILayout>
      <View style={styles.container}>
        {isLoading ? (
          <View style={styles.loaderWrap}>
            <Loader />
          </View>
        ) : (
          <AssessmentForm
            activityData={{
              activityState: ActivityState.Draft,
              scheduledDate: undefined,
              dueDate: null,
            }}
            onSave={handleAssessmentCreated}
            onCancel={handleCancel}
            error={formError}
            isSubmitting={isCompletingDeepLink}
            isNewActivity
            showFreeTextInstructionsInput={true}
            lmsAiTutors={lmsAiTutors}
          />
        )}
      </View>
    </LTILayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  loaderWrap: {
    marginTop: 24,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default LTIDeepLinkAssessmentView;
